import { Competition, Contact, Team } from "../../models";
import { formatStartEndDatesStrings } from "../dates/FormatDates";
import mainLogo from "../../assets/img/brand/MainLogo.png";
import moment from "moment";
import { addFooters } from "./ReportFooter";
import { openTabForPrinting } from "./ReportUtils";
import { formatAddress } from "../address/FormatAddress";
import { formatPhoneNumberForDisplay } from "../contact/FormatPhoneNumber";
import { getCompetitionStatus, getCompetitionStatusColor } from "../competition/CompetitionUtils";
import { getTeamById } from "../team/Team";

const { jsPDF } = require("jspdf");
require('jspdf-autotable');

const RSPFillColor = "#73a4d3";
function getClassesOfferedString(competition: Competition) {
    let classesString = "";
    if (competition.isOpenOffered) classesString = classesString + "Open, ";
    if (competition.isIntermediateOffered) classesString = classesString + "Intermediate, ";
    if (competition.isNoviceOffered) classesString = classesString + "Novice, ";
    if (competition.isAdvancedBeginnerOffered) classesString = classesString + "Advanced Beginner, ";
    if (competition.isBeginnerOffered) classesString = classesString + "Beginner, ";
    if (competition.isMinisOffered) classesString = classesString + "Minis, ";
    if (competition.isAdultOffered) classesString = classesString + "Adult";
    if (competition.isAELMedalOffered) classesString = classesString + ", AEL Medal";
    return classesString;
}

export async function generateAELCompetitionInvitationReport(competition: Competition, isOpenInNewTab: boolean = false) {
    // initialize jsPDF
    const doc = new jsPDF();

    // Header - Competition Name, Date and Location
    doc.setFontSize(16);
    doc.setFont("serif");
    doc.text(`Competition: ${competition.name}`, 14, 15);
    doc.setFontSize(12);

    let competitionStatus = getCompetitionStatus(competition);

    let hostTeamName = "";
    if (competition.teamId) {
        const teamQuery = await getTeamById(competition.teamId);
        if (teamQuery.isSuccess) {
            const team: Team = teamQuery.result;
            hostTeamName = team.name;
        }
    }

    // Competition Info Table
    const competitionTableColumns: string[] = ["Competition Info", ""];
    const competitionTableRows = [];
    const competitionDate = competition.startDate ? moment(competition.startDate).format("dddd MMMM D, YYYY") : "";
    competitionTableRows.push(["Competition Date", competitionDate]);
    competitionTableRows.push(["Location", competition.address ? formatAddress(competition.address) : ""]);
    competitionTableRows.push(["Host Team", hostTeamName]);
    competitionTableRows.push(["Status", competitionStatus]);
    doc.autoTable(competitionTableColumns, competitionTableRows, { 
        theme: "grid",
        headStyles: {fillColor: RSPFillColor},
        startY: 25
    });

    // Competition Organizer Table
    const organizerTableColumns: string[] = ["Competition Organizer", ""];
    const organizerTableRows = [];
    const organizerContact = competition.contact;
    const phoneNumber = organizerContact?.cell || organizerContact?.work || organizerContact?.home || "";
    organizerTableRows.push(["Organizer Name", competition.competitionOrganizerName || ""]);
    organizerTableRows.push(["Organizer Email", organizerContact?.workEmail || ""]);
    organizerTableRows.push(["Organizer Phone Number", formatPhoneNumberForDisplay(phoneNumber)]);
    doc.autoTable(organizerTableColumns, organizerTableRows, { 
        theme: "grid",
        headStyles: {fillColor: RSPFillColor},
        startY: doc.lastAutoTable.finalY + 5
    });
    
    // Competition Officials Table
    const officialsTableColumns: string[] = ["Competition Officials", ""];
    const officialsTableRows = [];
    officialsTableRows.push(["Judge Name", competition.judgeName || ""]);
    officialsTableRows.push(["Secretary Name", competition.secretaryName || ""]);
    officialsTableRows.push(["Steward Name", competition.StewardName || ""]);
    doc.autoTable(officialsTableColumns, officialsTableRows, { 
        theme: "grid",
        headStyles: {fillColor: RSPFillColor},
        startY: doc.lastAutoTable.finalY + 5
    });

    // Entry Info Table
    const entryTableColumns: string[] = ["Classes & Entry Info", ""];
    const entryTableRows = [];
    const classesString = getClassesOfferedString(competition);
    let entryFeePerRiderForMedal = "";
    if (!(!!competition.isAELMedalOffered)) entryFeePerRiderForMedal = "N/A";
    else if (competition.entryFeePerRiderForMedal) entryFeePerRiderForMedal = ("$" + competition.entryFeePerRiderForMedal.toFixed(2));
    else if (competition.entryFeePerRiderForMedal === 0) entryFeePerRiderForMedal = "$0";
    const entryDueDateTime = competition.entryClosingDate ? (competition.entryClosingTime ? moment((competition.entryClosingDate + " " + competition.entryClosingTime)).format("dddd MMM D, YYYY h:mm a") : moment(competition.entryClosingDate).format("dddd MMM DD YYYY")) : "";
    entryTableRows.push(["Classes Offered", classesString]);
    entryTableRows.push(["Entry Fee Per Rider", competition.entryFeePerRider ? ("$" + competition.entryFeePerRider.toFixed(2)) : ""]);
    entryTableRows.push(["AEL Medal Entry Fee Per Rider", entryFeePerRiderForMedal]);
    entryTableRows.push(["Entry Payment", competition.feePaymentOptions || ""]);
    entryTableRows.push(["Entry Due Date", entryDueDateTime || ""]);
    entryTableRows.push(["Entry Limitations", competition.isEntryLimitInPlace ? ("Yes - " + competition.entryLimitDescription) : "None"]);
    doc.autoTable(entryTableColumns, entryTableRows, { 
        theme: "grid",
        headStyles: {fillColor: RSPFillColor},
        startY: doc.lastAutoTable.finalY + 5
    });

    // Other Info Table
    const otherTableColumns: string[] = ["General Info"];
    const otherTableRows = [];
    otherTableRows.push([competition.otherNotes]);
    doc.autoTable(otherTableColumns, otherTableRows, { 
        theme: "grid",
        headStyles: {fillColor: RSPFillColor},
        startY: doc.lastAutoTable.finalY + 5
    });

    // Footer - date and time of generation and page footer.
    doc.setFontSize(10);
    doc.text(`Competition Invitation downloaded: ${moment().format("dddd MMMM DD, YYYY h:mm a")}`, 14, (doc.internal.pageSize.height - 15));

    doc.addPage();

    doc.setFontSize(16);
    doc.setFont("serif");
    doc.text(`Competition: ${competition.name}`, 14, 15);

    doc.setFontSize(12);
    doc.text(`Competition Date: ${competitionDate}`, 14, 22);
    doc.text(`Entry Due Date: ${entryDueDateTime}`, 104, 22);

    // First Table - Team Information
    const teamTableColumns: string[] = ["Team Name", "Phone #", "Email Address"];
    const teamTableRows = [];
    teamTableRows.push(["", "", ""]);
    doc.autoTable(teamTableColumns, teamTableRows, { 
        theme: "grid",
        headStyles: {fillColor: RSPFillColor},
        startY: 25
    });

    doc.text(`*Please list according to Division (all Beginner riders, then Novice riders, etc)`, 14, doc.lastAutoTable.finalY + 8);

    // Second Table - Riders Information
    const ridersTableColumns: string[] = ["Rider #", "Rider Full Name", "Rider Level", "Rider Grade", "Horse/Pony"];
    const ridersTableRows = [];
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    ridersTableRows.push(["", "", "", ""]);
    doc.autoTable(ridersTableColumns, ridersTableRows, { 
        theme: "grid",
        headStyles: {fillColor: RSPFillColor},
        startY: doc.lastAutoTable.finalY + 12
    });

    doc.text(`This entry constitutes an agreement and affirmation that the Athlete/Parent making it, the Club, and each`, 14, doc.lastAutoTable.finalY + 10);

    doc.text(`of its riders, coaches and trainers shall accept and abide by the rules of the AEL and of the Competition, `, 14, doc.lastAutoTable.finalY + 15);

    doc.text(`that each rider is eligible as entered, that they will accept as final any rulings of the AEL with respect`, 14, doc.lastAutoTable.finalY + 20);

    doc.text(`to their conduct and each entry agrees to defend and hold the AEL, its directors, officials, employees and`, 14, doc.lastAutoTable.finalY + 25);

    doc.text(`all persons, stables or other entities providing facilities, horses or equipment for the Competition, harmless `, 14, doc.lastAutoTable.finalY + 30);

    doc.text(`for any claim, action or suit, for injury, property damage or death sustained during participation in the show`, 14, doc.lastAutoTable.finalY + 35);

    doc.text(`by the entity, his or her coach, trainer or visitors.`, 14, doc.lastAutoTable.finalY + 40);

    doc.setFontSize(15);
    doc.text(`Coach Signature_______________________________________`, 14, doc.lastAutoTable.finalY + 50);
    doc.text(`Date__________________________________________________`, 14, doc.lastAutoTable.finalY + 60);

    doc.setFontSize(10);
    doc.text(`Competition Invitation downloaded: ${moment().format("dddd MMMM DD, YYYY h:mm a")}`, 14, (doc.internal.pageSize.height - 15));
    addFooters(doc);

    // We define the name of our PDF file.
    const fileName = competition.name + " Invitation.pdf";

    // Skip download and just open in new tab
    if (isOpenInNewTab) openTabForPrinting(doc, fileName);

    var blobPDF =  new Blob([ doc.output() ], { type : 'application/pdf'});
    return blobPDF;
}